import React, { useState, useEffect, useMemo, useRef } from 'react';
import useAppResume from '../../native/useAppResume';
import {
  Box, Typography, Grid, Card, CardMedia, CardContent, CardActions,
  Chip, Avatar, Button, TextField, MenuItem, Select, FormControl,
  InputLabel, Paper, InputAdornment, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Loader from '../../styles/Loader';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import GavelIcon from '@mui/icons-material/Gavel';
import FlightIcon from '@mui/icons-material/Flight';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { getNearbyListings } from '../../collection/collectionApi';
import PersonIcon from '@mui/icons-material/Person';
import { FEATURE_FLAGS } from '../../utils/featureFlags';
import { CONDITION_COLORS, conditionLabel } from '../../utils/cardPricing';
import { useCurrency } from '../../providers/currencyContext';
// An asking price is entered in the source currency and stored untagged, so it
// is rendered with that code explicitly rather than by defaulting.
import { SOURCE_CURRENCY } from '../../utils/currency';
import {
  DISCOVERY_OK,
  DISCOVERY_RATE_LIMITED,
  DISCOVERY_FAILED,
  DISCOVERY_OUT_OF_AREA,
} from '../../layout/components/firstTradeMission';
import { REVOKED_SESSION_CODES } from '../../utils/apiErrorUtils';
import { prefersReducedMotion } from '../../styles/particleField';
import { getDiscoveryRadiusMeters } from '../../utils/runtimeFlags';
import { collectorDisplayName } from '../collectorIdentity';


// Israel-first MVP: only "Nearby" is shown.
// Set REACT_APP_ENABLE_GLOBAL_MARKETPLACE=true to restore the worldwide region list.
const REGIONS_ISRAEL = [
  { label: 'Nearby', coords: null },
];
const REGIONS_GLOBAL = [
  { label: 'Nearby', coords: null },
  { label: 'New York, USA', coords: { lng: -74.006, lat: 40.7128 } },
  { label: 'London, UK', coords: { lng: -0.1276, lat: 51.5074 } },
  { label: 'Tokyo, Japan', coords: { lng: 139.6917, lat: 35.6895 } },
  { label: 'Tel Aviv, Israel', coords: { lng: 34.7818, lat: 32.0853 } },
  { label: 'Sydney, Australia', coords: { lng: 151.2093, lat: -33.8688 } },
  { label: 'Berlin, Germany', coords: { lng: 13.405, lat: 52.52 } },
  { label: 'Sao Paulo, Brazil', coords: { lng: -46.6333, lat: -23.5505 } },
];
const REGIONS = FEATURE_FLAGS.ENABLE_GLOBAL_MARKETPLACE ? REGIONS_GLOBAL : REGIONS_ISRAEL;

// No FALLBACK point (E2E audit E-24). A hard-coded Tel Aviv used to stand in
// for "we don't know where you are", so a collector in Haifa or Berlin saw Tel
// Aviv listings labelled nearby. An IP guess or a default point is no better.
const LISTINGS_NO_LOCATION = 'no_location';
const APPROXIMATE_SOURCES = new Set(['ip', 'default']);
// Was a hardcoded 100000, which the server silently clamped to its own 50 km
// ceiling — so the comment claiming a "100 km search radius" never described
// what happened. Reads the deployment's value instead, so the listings tab and
// the map reach the same distance and both follow the admin setting.
const searchRadiusMeters = () => getDiscoveryRadiusMeters();

/**
 * Quantise a coordinate before it becomes a query (P1-41).
 *
 * Two decimal places is roughly 1.1 km at the equator — about 1% of the 100 km
 * search radius, so no move that could meaningfully change which listings come
 * back is rounded away, while the metre-scale wobble a stationary phone reports
 * on every GPS fix is. The grid is a quantisation, not a distance threshold: a
 * collector standing on a cell boundary can still flip between two values, but
 * two is bounded, and the previous behaviour was unbounded.
 */
const COORD_GRID = 100;

// Tiles drawn per step (B-52). The server caps the feed at 200 rows; drawing
// every one at once made the first paint slow on a phone.
const TILE_STEP = 40;
const quantise = (n) => Math.round(n * COORD_GRID) / COORD_GRID;

/**
 * What to say when the tab has no listings to show and the reason is not
 * "there are none" (#82's vocabulary, applied to listings).
 *
 * A throttled request rendered as "No listings found" is a false negative on
 * the question the tab exists to answer, and it is unrecoverable — the user has
 * no reason to try again, because they were told there is nothing to find.
 */
const OUTCOME_COPY = {
  [LISTINGS_NO_LOCATION]: {
    headline: 'Location is off',
    body: "Listings are sorted by distance from you, and we don't know where you are. Turn on location to see what collectors near you have listed.",
    retry: null,
  },
  [DISCOVERY_RATE_LIMITED]: {
    headline: 'Search is taking a short break',
    body: "You've searched a lot in the last few minutes, so we paused for a moment. This says nothing about what collectors near you have listed — try again shortly.",
    retry: 'Try again',
  },
  [DISCOVERY_FAILED]: {
    headline: "We couldn't load nearby listings",
    body: "Something went wrong on our end or with your connection, so we don't know what's listed near you right now.",
    retry: 'Try again',
  },
  // #102. This endpoint sits behind the same launch-area gate as the other two
  // discovery surfaces, so it can be refused for a reason that has nothing to
  // do with anything going wrong. No `retry`: the effect replays the identical
  // quantised coordinates, and the gate is a pure bounds test.
  [DISCOVERY_OUT_OF_AREA]: {
    headline: "Tradeloft isn't open here yet",
    body: "The beta covers a limited area, and where you are isn't part of it yet. Nothing's wrong with your account — we just aren't showing listings here.",
    retry: null,
  },
};

/**
 * Which story a rejected listings load is (#82, #102).
 *
 * Ranked the same way MapPage ranks the map's two calls, and for the same
 * reason: a 403 that is not a revoked session is the geofence, which is
 * permanent, so it outranks the temporary throttle. `auth` runs before this
 * handler too, so the code check is what keeps a suspended account from being
 * told it is in the wrong place.
 */
const outcomeForListingsFailure = (error) => {
  if (error?.status === 403 && !REVOKED_SESSION_CODES.includes(error?.code)) {
    return DISCOVERY_OUT_OF_AREA;
  }
  if (error?.status === 429) return DISCOVERY_RATE_LIMITED;
  return DISCOVERY_FAILED;
};

/**
 * The asking price a listing can actually be sorted by, or `null` (#189).
 *
 * A listing with no price arrives in four shapes. The field can be absent. It
 * can be `null` — ListCardModal sends null for any collectionType that is not
 * 'sale', and the create path stores `askingPrice || null`. It can be `0` —
 * ListCardModal computes `parseFloat(askingPrice) || 0`, which is 0 when the
 * seller types nothing (#177), and the update path stores that verbatim. And
 * an API could hand back NaN. `askingPrice || 0` flattened all four into the
 * number zero, which is a claim the rest of the app never makes: every price
 * the UI prints is gated on `askingPrice > 0` — the card body below, and
 * BidModal. Same gate here, so the sort and the card agree about which
 * listings have a price at all.
 */
const usablePrice = (listing) => {
  const n = Number(listing?.askingPrice);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Order by price, with the priceless listings last in BOTH directions.
 *
 * "No price" is not a low price and it is not a high one — it is not on the
 * axis being sorted, so it cannot be ranked against listings that are. Sorting
 * it as zero put every priceless card ahead of the cheapest real one on
 * "Price: Low", which with the Type filter on All made the first screen of the
 * marketplace nothing but cards with no price. Priced listings keep exactly
 * the order they had; ties (including two priceless listings) return 0 and
 * Array#sort is stable, so the fetch order survives underneath.
 */
const byPrice = (direction) => (a, b) => {
  const pa = usablePrice(a);
  const pb = usablePrice(b);
  if (pa === null && pb === null) return 0;
  if (pa === null) return 1;
  if (pb === null) return -1;
  return direction * (pa - pb);
};

/**
 * The search box and a card name, folded the same way before comparing: case,
 * accents ("flabebe" finds Flabébé, "pokemon" Pokémon), apostrophes of any
 * shape dropped ("farfetchd" finds Farfetch'd), other punctuation and spaces
 * collapsed ("mr mime" finds Mr. Mime), ends trimmed (a keyboard suggestion
 * adds a space). Letters of every script are kept. E2E audit 18·1.
 */
const foldForSearch = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/['`\u2018\u2019\u02BC]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

/** A return to the app refreshes Listings at most this often. */
const RESUME_REFRESH_MIN_MS = 60 * 1000;

const MarketplaceView = ({ userLocation, onUserClick, onTradeListing, onBidListing, isRoaming = false, roamingCity = null, isActive = true }) => {
  const { formatAmount } = useCurrency();
  // Read at render, never at module scope: the preference can change while the
  // app is open, and a value captured at import time could never notice.
  const reduced = prefersReducedMotion();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outcome, setOutcome] = useState(DISCOVERY_OK);
  const [retryToken, setRetryToken] = useState(0);
  // E2E audit 18·16: every other list reloads when the collector comes back
  // to the app (B-50); this one kept showing what it had, so sold listings
  // stayed up. A return refreshes it quietly (the list stays on screen), and
  // at most once a minute, because the request spends the discovery budget.
  const lastFetchAtRef = useRef(0);
  const quietRefreshRef = useRef(false);
  useAppResume(() => {
    if (!isActive || Date.now() - lastFetchAtRef.current < RESUME_REFRESH_MIN_MS) return;
    quietRefreshRef.current = true;
    setRetryToken((t) => t + 1);
  });
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filterType, setFilterType] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState(0);
  const [shown, setShown] = useState(TILE_STEP);

  // Numbers, not an object (P1-41). `activeCoords` used to be memoised on the
  // `userLocation` OBJECT, and the geolocation watch hands MapPage a brand new
  // position object on every fix — so the memo produced a new identity each
  // time, the fetch effect saw a changed dependency, and the tab re-requested
  // while the collector sat still. Those requests spend the shared discovery
  // budget, and the 429 that eventually came back was then rendered as an empty
  // neighbourhood. Primitives compare by value, so an unchanged position is now
  // literally an unchanged dependency.
  const region = REGIONS[selectedRegion];
  const located =
    Number.isFinite(userLocation?.coords?.longitude) &&
    Number.isFinite(userLocation?.coords?.latitude) &&
    !APPROXIMATE_SOURCES.has(userLocation?.source);
  // A city chosen with the roaming search is where the Map is looking, so
  // Listings looks there too (code review LOCALE-5). It used to search around
  // the user's own position while the Map showed another city.
  const roam =
    isRoaming && Number.isFinite(roamingCity?.longitude) && Number.isFinite(roamingCity?.latitude)
      ? { lng: roamingCity.longitude, lat: roamingCity.latitude }
      : null;
  const rawLng = region?.coords ? region.coords.lng : roam ? roam.lng : located ? userLocation.coords.longitude : null;
  const rawLat = region?.coords ? region.coords.lat : roam ? roam.lat : located ? userLocation.coords.latitude : null;
  const hasPoint = rawLng !== null && rawLat !== null;
  const queryLng = hasPoint ? quantise(rawLng) : null;
  const queryLat = hasPoint ? quantise(rawLat) : null;

  useEffect(() => {
    // The tab is one of several views on the map screen. Nothing it fetches is
    // worth spending the shared discovery budget on while nobody is looking at
    // it, and the component must not depend on its parent choosing to unmount
    // it to make that true.
    // Read once and cleared on every run, so a resume flag can never leak into
    // a later fetch (an inactive tab or a lost point returns early below).
    const quiet = quietRefreshRef.current;
    quietRefreshRef.current = false;
    if (!isActive) return undefined;
    if (!hasPoint) {
      // Nothing to search around: say so, and spend no discovery budget.
      setListings([]);
      setOutcome(LISTINGS_NO_LOCATION);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    lastFetchAtRef.current = Date.now();
    if (!quiet) setLoading(true);
    // The quantised pair is what gets sent, so the request and the dependency
    // that triggered it always agree.
    getNearbyListings(queryLng, queryLat, searchRadiusMeters())
      .then((data) => {
        if (cancelled) return;
        setListings(Array.isArray(data) ? data : []);
        setOutcome(DISCOVERY_OK);
      })
      .catch((error) => {
        if (cancelled) return;
        // A background refresh that fails keeps what is on screen; it was
        // only ever an attempt to freshen it (release review).
        if (quiet) return;
        setListings([]);
        setOutcome(outcomeForListingsFailure(error));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hasPoint is queryLng !== null
  }, [queryLng, queryLat, isActive, retryToken]);

  const outcomeCopy = outcome === DISCOVERY_OK ? null : OUTCOME_COPY[outcome];
  // Set by getNearbyListings when the server held listings back.
  const capped = listings.cappedAt;

  const filtered = useMemo(() => {
    let result = [...listings];

    const q = foldForSearch(search);
    if (q) {
      result = result.filter((l) =>
        foldForSearch(l.cardId?.name).includes(q) ||
        foldForSearch(l.cardId?.rarity).includes(q)
      );
    }

    if (filterType !== 'all') {
      result = result.filter((l) => l.collectionType === filterType);
    }

    switch (sortBy) {
      case 'price_low':
        result.sort(byPrice(1));
        break;
      case 'price_high':
        result.sort(byPrice(-1));
        break;
      case 'newest':
      default:
        result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
    }

    return result;
  }, [listings, search, sortBy, filterType]);

  // The same name, built the same way, as the map pin and the profile sheet
  // (collectorDisplayName), so one person is never "User" here and
  // "Collector" one tap later.
  const getSellerName = (item) => collectorDisplayName(item.userId);

  // The seller as the profile sheet (onUserClick) and the arena expect them.
  const sellerOf = (item) => ({
    id: item.userId?._id,
    _id: item.userId?._id,
    username: getSellerName(item),
    profilePicture: item.userId?.image?.url,
    coordinates: item.userId?.coordinates || [],
  });

  return (
    <Box sx={{ px: 2, pb: 10 }}>
      {/* Travel / Region Selector — only shown when global marketplace is enabled */}
      {REGIONS.length > 1 && (
        <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 3, bgcolor: '#f5f0ff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <FlightIcon sx={{ color: '#6A1B6D' }} />
            <Typography variant="subtitle2" fontWeight="bold">Explore Regions</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
            {REGIONS.map((r, i) => (
              <Chip
                key={r.label}
                label={r.label}
                size="small"
                onClick={() => setSelectedRegion(i)}
                variant={selectedRegion === i ? 'filled' : 'outlined'}
                color={selectedRegion === i ? 'secondary' : 'default'}
                sx={{ fontWeight: selectedRegion === i ? 700 : 400 }}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Search and Filters */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search cards..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 150 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Type</InputLabel>
          <Select value={filterType} label="Type" onChange={(e) => setFilterType(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="sale">For Sale</MenuItem>
            <MenuItem value="trade">For Trade</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Sort</InputLabel>
          <Select value={sortBy} label="Sort" onChange={(e) => setSortBy(e.target.value)}>
            <MenuItem value="newest">Newest</MenuItem>
            <MenuItem value="price_low">Price: Low</MenuItem>
            <MenuItem value="price_high">Price: High</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* A count is a claim about the neighbourhood. When the load did not
          succeed we do not have one, and "0 listings found" would be the same
          lie the empty panel used to tell. */}
      {/* B-52: past the server's cap the count, an empty search and the price
          sort cover only the 200 it sent (each seller's newest, shared out
          between sellers), so say so. */}
      {!outcomeCopy && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {filtered.length} listing{filtered.length !== 1 ? 's' : ''} found
          {capped ? ` in ${capped} recent listings nearby` : ''}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Message sellers directly to arrange trades or purchases locally.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Loader context="grid" label="Loading listings" />
        </Box>
      ) : outcomeCopy ? (
        // Checked BEFORE the empty branch on purpose: a throttled or failed
        // request must never fall through to "No listings found" (P1-41).
        <Paper data-testid="listings-outcome" data-outcome={outcome} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" color="text.secondary" data-testid="listings-outcome-headline">
            {outcomeCopy.headline}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {outcomeCopy.body}
          </Typography>
          {/*
            Gated on the copy's own `retry`, not rendered unconditionally: an
            outcome the user cannot recover from must not be handed a button
            that replays the same defined-to-fail request (#102).
          */}
          {outcomeCopy.retry && (
            <Button
              size="small"
              variant="contained"
              data-testid="listings-outcome-retry"
              onClick={() => setRetryToken((t) => t + 1)}
              sx={{ mt: 2 }}
            >
              {outcomeCopy.retry}
            </Button>
          )}
        </Paper>
      ) : filtered.length === 0 ? (
        <Paper data-testid="listings-empty" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" color="text.secondary">No listings found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {/* There is only one region while roaming is off, so advising a
                region change sent people looking for a control that isn't there. */}
            {capped
              ? `Only ${capped} recent listings nearby are searched, the newest from each collector. Try a different search or filter.`
              : 'Try adjusting your filters, or check back as more collectors join'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filtered.slice(0, shown).map((item) => {
            const card = item.cardId || {};
            return (
              <Grid item xs={6} sm={4} md={3} key={item._id}>
                {/* The hover lift and its deeper shadow both stay — the card
                    still reports which listing the pointer is over, it just
                    arrives there in one frame instead of gliding. */}
                <Card sx={{ borderRadius: 2, transition: reduced ? 'none' : 'transform 0.2s, box-shadow 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}>
                  <CardMedia
                    component="img"
                    image={card.images?.small || 'https://placehold.co/200x280?text=Card'}
                    alt={card.name}
                    sx={{ height: 200, objectFit: 'contain', bgcolor: '#f5f5f5', pt: 1 }}
                  />
                  <CardContent sx={{ pb: 0.5, pt: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold" noWrap>{card.name || 'Unknown Card'}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      <Chip
                        label={item.collectionType === 'sale' ? 'Sale' : 'Trade'}
                        size="small"
                        color={item.collectionType === 'sale' ? 'success' : 'info'}
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                      {/* `|| '#999'` (#282). This was the only one of the nine
                          CONDITION_COLORS reads in the app without a fallback.
                          `color` is a hard-coded white one property along, so a
                          grade the map has no own property for left MUI's own
                          default chip fill — measured `rgba(0, 0, 0, 0.08)`,
                          not transparent — under white text, about 1.2:1. `#999`
                          is UserSidebar.jsx:188's hex, and copying the nearest
                          sibling is the whole of the reason for it. Counted, not
                          assumed: `git grep -n 'CONDITION_COLORS\[' -- react-app/src`
                          minus test files and one comment line gives NINE reads,
                          all of which now carry a fallback — `#94a3b8` on five
                          (CardProfileModal, TradeContentsSummary, TradeRequestModal
                          x3), `#999` on three (UserSidebar, UserProfile, this one)
                          and `#6b21a8` on ListCardModal. There is no single house
                          answer to copy.

                          TWO SENTENCES THAT STOOD HERE ARE RETRACTED — both
                          were false, both were shipped in e878c01, and both are
                          carried by backlog [#284] (repaired by fix sprint 103,
                          which re-measured each one rather than inheriting it):

                          · "Deliberately NOT damaged's #9e9e9e, so ungradeable
                            stays distinct from damaged." FALSE.
                            dE*ab(#999999, #9e9e9e) = 1.892, BELOW the ~2.3
                            practical just-noticeable difference, so on screen
                            the two chips are the same grey. For scale, in the
                            same CIELAB computation #94a3b8 — the app's other
                            five fallbacks — is dE 12.69 from damaged.

                          · "Same surface family, same chip-over-card-art
                            treatment [as UserSidebar.jsx:188]." FALSE in the
                            half that matters. That chip is
                            `position: 'absolute'` ON the card <img> AND is
                            gated at UserSidebar.jsx:184 by
                            `{item.condition && (`, so its `|| '#999'` can never
                            fire for an absent grade. This chip sits inside
                            THIS file's `<CardContent>` on white paper and is
                            ungated — cited by element rather than by line,
                            because editing this very comment moves any line
                            number written into it, which is how the two cites
                            in the sibling test file rotted. So this is the site
                            where the fallback actually renders.

                          The colour is deliberately UNCHANGED here: which hex
                          an unknown grade should take is a visual-design call,
                          and it is written up with three options in [#284]. */}
                      <Chip
                        label={conditionLabel(item.condition)}
                        size="small"
                        sx={{ height: 20, fontSize: '0.65rem', bgcolor: CONDITION_COLORS[item.condition] || '#999', color: '#fff' }}
                      />
                    </Box>
                    {item.collectionType === 'sale' && item.askingPrice > 0 && (
                      <Typography variant="h6" fontWeight="bold" color="success.main" sx={{ mt: 0.5 }}>
                        {formatAmount(item.askingPrice, SOURCE_CURRENCY)}
                      </Typography>
                    )}
                    <Divider sx={{ my: 0.5 }} />
                    {/* The seller's name opens their sheet (Chat, profile,
                        block, report) on every listing. A trade listing's
                        Trade opens the arena now (B-58), so this is its way
                        to the seller. */}
                    <Box
                      component="button"
                      type="button"
                      aria-label={`View ${getSellerName(item)}'s profile`}
                      onClick={() => onUserClick?.(sellerOf(item))}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minHeight: 36,
                        p: 0, border: 0, bgcolor: 'transparent', font: 'inherit', cursor: 'pointer',
                      }}
                    >
                      <Avatar
                        src={item.userId?.image?.url}
                        sx={{ width: 20, height: 20, fontSize: '0.6rem' }}
                      >
                        {/* Array.from: an emoji-first name must not be cut mid-character. */}
                        {Array.from(getSellerName(item))[0]}
                      </Avatar>
                      <Typography variant="caption" color="primary" noWrap>
                        <bdi>{getSellerName(item)}</bdi>
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ pt: 0, px: 1, pb: 1 }}>
                    {item.collectionType === 'sale' && FEATURE_FLAGS.ENABLE_BIDDING ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<GavelIcon />}
                        fullWidth
                        onClick={() => {
                          if (onBidListing) {
                            onBidListing(item);
                          } else {
                            onUserClick?.(sellerOf(item));
                          }
                        }}
                      >
                        Bid
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={item.collectionType === 'trade' ? <SwapHorizIcon /> : <PersonIcon />}
                        fullWidth
                        onClick={() => {
                          // "Trade" opens the arena with THIS card already
                          // requested (B-58). It used to open the seller's
                          // sheet, whose Trade then opened an EMPTY arena.
                          if (item.collectionType === 'trade' && onTradeListing) {
                            onTradeListing(sellerOf(item), item);
                          } else {
                            onUserClick?.(sellerOf(item));
                          }
                        }}
                      >
                        {/* "View Seller" opens the seller's profile sheet, where
                            the Chat action lives. The previous label promised
                            a conversation the button does not start. */}
                        {item.collectionType === 'trade' ? 'Trade' : 'View Seller'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
          {filtered.length > shown && (
            <Grid item xs={12} sx={{ textAlign: 'center' }}>
              <Button variant="outlined" data-testid="listings-show-more" onClick={() => setShown((n) => n + TILE_STEP)}>
                Show more
              </Button>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default MarketplaceView;
